// The storefront preview canvas — the `.storefront-editor` box every page editor draws its preview
// inside — with link navigation neutralized.
//
// The preview is built from the same markup the storefront ships, which means it contains real
// anchors: footer links, header menu items, the banner CTA, social icons, affiliation logos, and
// whatever a merchant types into a rich-text or markdown field. Left alone they navigate. In an
// editor that is never what the click meant: a merchant clicking a footer link is inspecting how it
// looks, and being thrown onto another page (or into a new tab, for the `target="_blank"` ones)
// loses their place and, in edit mode, any unsaved work on the way out.
//
// The interception is deliberately narrow:
//   * `preventDefault` only — never `stopPropagation`. The element's own React handlers still run,
//     so clicking a link that also opens a style popover still opens it. Cancelling the default is
//     the whole job; swallowing the event would break the editor's click-to-edit affordances.
//   * Anchors only (`closest('a[href]')`). Buttons, pencils and every other editor control are
//     untouched, and an anchor with no href was never going to navigate anyway.
//   * Hover is not affected at all. `:hover` rules and mouse handlers fire exactly as before, so
//     the merchant still sees the link's real hover styling — which is usually the thing they
//     clicked in to check.
//
// Capture phase so it lands before any handler that might navigate imperatively, and `auxclick` as
// well as `click` because a middle-click on an anchor opens a background tab without ever firing a
// normal click.
//
// This replaces per-anchor `onClick={(e) => e.preventDefault()}`, which several previews had and
// several others had forgotten — and which could never have covered the anchors that markdown and
// rich-text fields generate at render time, since those exist in no source file.

import React from 'react';

export interface IPreviewCanvasProps extends React.HTMLAttributes<HTMLDivElement> {
    children?: React.ReactNode;
}

const swallowNavigation = (e: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (e.target as HTMLElement | null)?.closest?.('a[href]');
    if (anchor) e.preventDefault();
};

const PreviewCanvas: React.FC<IPreviewCanvasProps> = ({ className, children, ...rest }) => {
    const ref = React.useRef<HTMLDivElement>(null);

    // Publish a hundredth of the canvas width as `--sf-vw`, the storefront's stand-in for `1vw`
    // (declared as exactly that in ac-storefront-web's index.css).
    //
    // A merchant full-bleeds an element with `calc(50% - 50 * var(--sf-vw))`. On the storefront the
    // page IS the viewport, so that is a plain viewport breakout. Here it is not: the canvas is a
    // panel inside the admin chrome, so real `vw` measures the browser WINDOW — a full-bleed bar
    // overshoots the canvas on both sides and the overflow clip takes its rounded end caps with it,
    // which is why such a bar previewed with no caps at all. Re-basing the unit to the canvas makes
    // the same CSS land on the canvas edges here and the viewport edges there.
    //
    // clientWidth (content box, excluding the canvas border) is the width page content actually
    // spans, which is the quantity `vw` stands for on the storefront.
    React.useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const publish = () => {
            const width = el.clientWidth;
            // A zero width means the canvas is display:none or not laid out yet; writing 0 would
            // collapse every bleed to nothing, so leave the last good value in place.
            if (width > 0) el.style.setProperty('--sf-vw', `${width / 100}px`);
        };
        publish();
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(publish);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            {...rest}
            ref={ref}
            className={className ? `storefront-editor ${className}` : 'storefront-editor'}
            onClickCapture={swallowNavigation}
            onAuxClickCapture={swallowNavigation}
        >
            {children}
        </div>
    );
};

export default PreviewCanvas;
